import type { Characteristic, Component, LocalizedString, Rule } from '../schema';
import type { EvaluatedMap } from '../eval/types';
import type { SelectionsMap } from '../state/selections';
import { buildAltTitleIndex, evaluateCondition } from './condition';
import {
    EMPTY_RULE_RESULT,
    type PredefinedSelection,
    type RuleError,
    type RuleResult,
} from './types';

/**
 * Top-level entry: convert (Component + current Selections) into a frozen
 * {@link RuleResult}. Pure function — same input always produces an
 * output that compares equal by deep equality (Sets are rebuilt fresh
 * each call but that's a non-issue for React useMemo callers).
 *
 * Reads from BOTH the `rules` and `ruleGroups` arrays — legacy data
 * (10289) has all rules in ruleGroups[*].rules with rules[]=[]. The
 * 10244 fixture has them in both.
 */
export function evaluateRules(
    component: Component,
    selections: SelectionsMap,
    evaluated?: EvaluatedMap,
): RuleResult {
    const allCharacteristics = component.groups.flatMap((g) => g.characteristics);
    if (allCharacteristics.length === 0) return EMPTY_RULE_RESULT;

    const index = buildAltTitleIndex(allCharacteristics);
    const altToId = new Map<string, number>();
    const knownIds = new Set<number>();
    for (const c of allCharacteristics) {
        if (c.altTitle) altToId.set(c.altTitle, c.id);
        knownIds.add(c.id);
    }

    const hiddenCharacteristics = new Set<number>();
    const blacklistedValues = new Set<number>();
    const predefinedSelections: Record<number, PredefinedSelection> = {};
    const errors: Array<RuleError & { weight: number }> = [];
    const warnings: Array<RuleError & { weight: number }> = [];

    const allRules = [
        ...component.rules,
        ...component.ruleGroups.flatMap((rg) => rg.rules),
    ];

    /*
     * Weight decides who has the last word.
     *
     * Order only ever mattered in two places: a predefine for a
     * Characteristic overwrites the one before it (see collectPredefines),
     * and messages are listed as they are collected. So the heaviest rule is
     * evaluated LAST — that is what makes it win a predefine conflict — while
     * the message lists are turned around afterwards so the heaviest rule is
     * read first. Blacklists are a union and don't care either way.
     *
     * `sort` is stable in every engine we support, so rules of equal weight
     * keep their wire order: a Component that sets no weights evaluates
     * exactly as it did before this existed.
     */
    const orderedRules = [...allRules].sort((a, b) => a.weight - b.weight);

    for (const rule of orderedRules) {
        const matches = evaluateCondition(rule.rule, selections, index, evaluated);
        if (!matches) continue;

        switch (rule.validationType) {
            case 'blacklist':
                applyBlacklist(rule, altToId, knownIds, hiddenCharacteristics, blacklistedValues);
                break;
            case 'error': {
                // The admin's "Meldungstyp" decides whether this blocks
                // Add-to-Cart (error) or only informs (warning), as in legacy.
                const severity = rule.errorType;
                const entry = {
                    ...buildError(rule, severity, (ref) => placeholderValue(ref, index.byAltTitle, selections, evaluated)),
                    weight: rule.weight,
                };
                (severity === 'warning' ? warnings : errors).push(entry);
                break;
            }
            case 'warning':
                warnings.push({ ...buildError(rule, 'warning'), weight: rule.weight });
                break;
            case 'predefine_values':
                collectPredefines(rule, predefinedSelections);
                break;
        }
    }

    return {
        hiddenCharacteristics,
        blacklistedValues,
        predefinedSelections,
        // Heaviest first for reading, even though it was evaluated last.
        errors: byWeightDescending(errors),
        warnings: byWeightDescending(warnings),
    };
}

function applyBlacklist(
    rule: Rule,
    altToId: Map<string, number>,
    knownIds: Set<number>,
    hiddenCharacteristics: Set<number>,
    blacklistedValues: Set<number>,
): void {
    // Wire-format quirk: `blacklist_options` may carry either alt_titles
    // (string) or Characteristic IDs (number) — sometimes both formats
    // coexist in the same component. Try alt_title first; on miss, parse
    // as a numeric ID and accept it if the Component actually has that
    // Characteristic.
    for (const ref of rule.blacklistCharacteristics) {
        const byAlt = altToId.get(ref);
        if (byAlt !== undefined) {
            hiddenCharacteristics.add(byAlt);
            continue;
        }
        const asId = Number(ref);
        if (Number.isFinite(asId) && knownIds.has(asId)) {
            hiddenCharacteristics.add(asId);
        }
    }
    for (const valueIdStr of rule.blacklistValues) {
        const n = Number(valueIdStr);
        if (Number.isFinite(n)) blacklistedValues.add(n);
    }
}

/**
 * Transcribe a matching rule's `predefine_values` map into candidates — the
 * mode is carried through untouched, `resolvePredefineWrites` decides what it
 * means.
 *
 * Mode `'0'` (Disabled) is dropped here and nowhere recorded: legacy skips
 * those entries when it builds the rule's dependencies
 * (`if (!obj.hasOwnProperty('condition') || obj.condition != 0)`,
 * app/storage.js:884), so they never reach its apply path either. That is why
 * `'0'` is the value of every Characteristic the admin simply left alone —
 * 2618 of the 2759 entries in the local components — and why treating it as
 * "hide this Characteristic" made a single rule blank out most of a page.
 * Hiding is a `blacklist` rule's job, as in legacy.
 */
function collectPredefines(
    rule: Rule,
    out: Record<number, PredefinedSelection>,
): void {
    // Wire format: `predefine_values: { "<characteristicId>": { condition, value? } }`.
    // Keys are numeric strings keyed by Characteristic ID (NOT alt_title).
    for (const [charIdStr, entry] of Object.entries(rule.predefineValues)) {
        if (entry.condition === '0') continue;
        const characteristicId = Number(charIdStr);
        if (!Number.isFinite(characteristicId)) continue;
        if (entry.value === undefined) continue;

        // A `dynamicValue` target is an alt_title reference, not a Value ID,
        // so it stays `undefined` here — recorded anyway so the resolver can
        // skip it out loud rather than by way of `Number('breite') === NaN`.
        const parsed = Number(entry.value);
        const valueId = Number.isFinite(parsed) ? parsed : undefined;
        if (valueId === undefined && !entry.dynamicValue) continue;

        out[characteristicId] = {
            condition: entry.condition,
            valueId,
            dynamicValue: entry.dynamicValue,
        };
    }
}

/** Stable: equal weights keep the order they were collected in. */
function byWeightDescending(list: Array<RuleError & { weight: number }>): RuleError[] {
    return [...list]
        .sort((a, b) => b.weight - a.weight)
        .map(({ weight: _weight, ...error }) => error);
}

function buildError(
    rule: Rule,
    severity: 'error' | 'warning',
    lookup?: (ref: string) => string | undefined,
): RuleError {
    const raw = rule.errorMessage ?? `Rule ${rule.id}`;
    return {
        characteristic: rule.errorCharacteristic,
        message: lookup ? fillPlaceholders(raw, lookup) : raw,
        severity,
        ruleId: rule.id,
        whenExecute: rule.errorWhenExecute,
    };
}

/**
 * `$breite` / `$$breite` in a message become the Characteristic's current
 * value, as legacy's Rule.prepare does. Unknown names stay as written so a
 * dollar amount like "$5" in a message is left alone.
 */
function fillPlaceholders(
    message: string | LocalizedString,
    lookup: (ref: string) => string | undefined,
): string | LocalizedString {
    const fill = (text: string) =>
        text.replace(/\${1,2}([A-Za-z_][\w]*)/g, (match, ref: string) => lookup(ref) ?? match);
    if (typeof message === 'string') return fill(message);
    const out: Record<string, string> = {};
    for (const [locale, text] of Object.entries(message)) {
        if (typeof text === 'string') out[locale] = fill(text);
    }
    return out as LocalizedString;
}

function placeholderValue(
    ref: string,
    byAltTitle: ReadonlyMap<string, Characteristic>,
    selections: SelectionsMap,
    evaluated: EvaluatedMap | undefined,
): string | undefined {
    const c = byAltTitle.get(ref);
    if (!c) return undefined;
    if (evaluated && ['expression', 'combi', 'matrixvalue', 'productattribute', 'webservice'].includes(c.type)) {
        const v = evaluated.get(c.id);
        return v == null ? '' : String(v);
    }
    const sel = selections[c.id];
    if (!sel) return '';
    const valueOf = (id: number) => {
        const values = 'values' in c && Array.isArray(c.values) ? c.values : [];
        const found = values.find((v) => v.id === id);
        return found?.value ?? '';
    };
    switch (sel.kind) {
        case 'single':
            return sel.valueId == null ? '' : valueOf(sel.valueId);
        case 'multi':
            return sel.valueIds.map(valueOf).join(', ');
        case 'text':
            return sel.value;
        case 'quantities':
            return String(Object.values(sel.quantities).reduce((sum, q) => sum + q, 0));
    }
}
